import React from 'react';
import styled from 'styled-components';
import { downloadTokenCard, generateTokenCard, type TokenCardData } from '../../../utils/generateTokenCard';
import { useWalletClient } from 'wagmi';
import { mintCreatorCard } from '../../../utils/mintCreatorCard';
import { ethers } from 'ethers6';
import { generateAndUploadNFTMetadata } from '../../../utils/generateNFTMetadata';
import { supabase } from '../../../utils/supabase';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseAll?: () => void; // Optional callback to close parent modal
  tokenData: {
    name: string;
    symbol: string;
    contractAddress: string;
    logo_url?: string | null;
    description?: string | null;
    deployer_address?: string;
    created_at?: string;
    pool_address?: string | null;
    nft_token_id?: string | null;
    card_url?: string | null;
    privateKey?: string;
    walletAddress?: string;
    minted?: boolean;
  };
}

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 10001;
`;

const ModalContainer = styled.div`
  background: #0c0814;
  border: 2px solid #9535fc;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
`;

const Header = styled.h2`
  color: #21c95e;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 18px;
  margin: 0 0 16px 0;
  text-align: center;
`;

const Message = styled.p`
  color: #fffeff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  margin-bottom: 20px;
  text-align: center;
  line-height: 1.5;
`;

const TokenInfo = styled.div`
  background: rgba(149, 53, 252, 0.1);
  border: 1px solid #9535fc;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 20px;
`;

const TokenName = styled.div`
  color: #9535fc;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 20px;
  margin-bottom: 4px;
  text-align: center;
`;

const TokenAddress = styled.div`
  color: #b0b0b0;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  word-break: break-all;
  text-align: center;
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 4px;
`;

const ShareButton = styled.button`
  background: #7c3aed;
  color: #fffeff;
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;
  width: 100%;

  &:hover {
    background: #8b5cf6;
  }
`;

const CopyButton = styled(ShareButton)`
  background: #7c3aed;
`;

const IconWrapper = styled.span`
  display: inline-block;
  margin-right: 8px;
  font-size: 16px;
`;

const CloseButton = styled.button`
  background: transparent;
  color: #fffeff;
  border: 2px solid #fffeff;
  border-radius: 8px;
  padding: 12px 20px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
`;

// Clean, minimal panels (purple) used for wallet address and private key
const Panel = styled.div`
  background: #24102f;
  border: none;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
`;

const PanelTitle = styled.div`
  color: #c9b5ff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 8px;
`;

const PanelValue = styled.div`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  text-align: center;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  margin-bottom: 10px;
  word-break: break-all;
`;

const PanelHelp = styled.div`
  color: #b9a8ff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  text-align: center;
`;

const CopyKeyButton = styled.button`
  background: #ff453a;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 10px 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;
  display: block;
  margin: 10px auto 0 auto; /* centers button */

  &:hover {
    background: #cc3620;
  }
`;

const ButtonSubtitle = styled.span`
  display: block;
  font-weight: 500;
  font-size: 12px;
  color: #e9d5ff;
  margin-top: 2px;
`;

export const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, onCloseAll, tokenData }) => {
  const [copiedLink, setCopiedLink] = React.useState(false);
  const [copiedCard, setCopiedCard] = React.useState(false);
  const [copiedKey, setCopiedKey] = React.useState(false);
  const [isMinting, setIsMinting] = React.useState(false);
  const [xHandle, setXHandle] = React.useState<string | null>(null);
  const { data: walletClient } = useWalletClient();
  
  const handleClose = () => {
    onClose(); // Close this modal
    if (onCloseAll) {
      onCloseAll(); // Close parent modal if callback provided
    }
  };

  const handleMintCard = async () => {
    if (!walletClient || !tokenData.contractAddress || !tokenData.deployer_address) {
      alert('Please connect your wallet to mint your creator card');
      return;
    }

    try {
      setIsMinting(true);
      
      // First, generate and upload NFT metadata JSON
      console.log('🎨 Generating NFT metadata...');
      await generateAndUploadNFTMetadata(tokenData.contractAddress, {
        symbol: tokenData.symbol,
        logo_url: tokenData.logo_url || null,
        description: tokenData.description || null,
        deployer_address: tokenData.deployer_address,
        created_at: tokenData.created_at || new Date().toISOString(),
        pool_address: tokenData.pool_address || null,
        nft_token_id: tokenData.nft_token_id || null
      });
      
      // Then mint the NFT
      const provider = new ethers.BrowserProvider(window.ethereum);
      const txHash = await mintCreatorCard(provider, {
        tokenAddress: tokenData.contractAddress,
        deployerAddress: tokenData.deployer_address,
        edition: 1
      });

      alert(`Card minted successfully! Transaction: ${txHash}`);
      
      // Update the minted status in the database
      // Use upsert to create the record if it doesn't exist
      const { error } = await supabase
        .from('token_cards')
        .upsert({ 
          contract_address: tokenData.contractAddress,
          minted: true,
          deployer_address: tokenData.deployer_address || undefined,
          card_url: tokenData.card_url || null,
          updated_at: new Date().toISOString()
        }, { 
          onConflict: 'contract_address',
          ignoreDuplicates: false 
        });
      
      if (error) {
        console.error('Failed to update minted status:', error);
      } else {
        console.log('✅ Minted status updated in database');
      }
      
    } catch (error: any) {
      console.error('Failed to mint card:', error);
      alert(error.message || 'Failed to mint card. Please try again.');
    } finally {
      setIsMinting(false);
    }
  };

  const handleDownloadCard = async () => {
    try {
      const cardData: TokenCardData = {
        logo_url: tokenData.logo_url || null,
        symbol: tokenData.symbol,
        description: tokenData.description || null,
        deployer_address: tokenData.deployer_address || '',
        created_at: tokenData.created_at || new Date().toISOString(),
        pool_address: tokenData.pool_address || null,
        nft_token_id: tokenData.nft_token_id || null,
      };
      
      await downloadTokenCard(cardData, `${tokenData.symbol}-token-card.png`);
      console.log('Token card downloaded successfully');
    } catch (error) {
      console.error('Error downloading token card:', error);
      alert('Failed to download token card. Please try again.');
    }
  };

  const handleCopyCardToClipboard = async () => {
    try {
      setCopiedCard(true);
      
      const cardData: TokenCardData = {
        logo_url: tokenData.logo_url || null,
        symbol: tokenData.symbol,
        description: tokenData.description || null,
        deployer_address: tokenData.deployer_address || '',
        created_at: tokenData.created_at || new Date().toISOString(),
        pool_address: tokenData.pool_address || null,
        nft_token_id: tokenData.nft_token_id || null,
      };
      
      // Generate the card as a blob
      const blob = await generateTokenCard(cardData);
      
      // Copy to clipboard
      const clipboardItem = new ClipboardItem({ 'image/png': blob });
      await navigator.clipboard.write([clipboardItem]);
      
      console.log('Token card copied to clipboard');
      
      // Reset copied state after 2 seconds
      setTimeout(() => setCopiedCard(false), 2000);
    } catch (error) {
      console.error('Error copying token card:', error);
      setCopiedCard(false);
      
      // Fallback: if clipboard API fails, download instead
      alert('Clipboard API not available. Downloading card instead...');
      handleDownloadCard();
    }
  };

  const buyLink = `https://mavrk.ink/swap?buy=${tokenData.contractAddress}`;

  // Fetch X/Twitter handle for the token (optional) from Supabase
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await supabase
          .from('tokens')
          .select('twitter')
          .eq('contract_address', tokenData.contractAddress)
          .maybeSingle();
        if (!mounted) return;
        const url: string | null = (data?.twitter as string) || null;
        if (url) {
          // Extract handle from https://x.com/handle or https://twitter.com/handle or @handle
          const atMatch = url.match(/@([A-Za-z0-9_]{1,15})/);
          const pathMatch = url.match(/(?:x|twitter)\.com\/(?:#!\/)?@?([A-Za-z0-9_]{1,15})/i);
          const handle = (atMatch?.[1] || pathMatch?.[1] || '').trim();
          setXHandle(handle || null);
        } else {
          setXHandle(null);
        }
      } catch {
        setXHandle(null);
      }
    })();
    return () => { mounted = false; };
  }, [tokenData.contractAddress]);

  // Link-free main tweet per X algorithm recommendations
  const tweetText = (() => {
    const base = `I just launched $${tokenData.symbol} using @MavrkOfficial on @inkonchain!\n\nContract: ${tokenData.contractAddress}`;
    return xHandle ? `${base}\n\nX: @${xHandle}` : base;
  })();

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;

  const handleShare = (platform: 'twitter') => {
    if (platform === 'twitter') {
      window.open(twitterUrl, '_blank');
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(buyLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(tokenData.contractAddress);
      // Don't need to show "Copied!" here, just copy silently
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyPrivateKey = async () => {
    if (!tokenData.privateKey) return;
    try {
      await navigator.clipboard.writeText(tokenData.privateKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay isOpen={isOpen} onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>Token Successfully Deployed!</Header>
        
        <Message>
          Share your token and let others know about <strong>{tokenData.symbol}</strong>
        </Message>

        {/* Wallet-less deployment details in a clean layout */}
        {tokenData.privateKey && tokenData.walletAddress && (
          <>
            <Panel>
              <PanelTitle>Your Creator Wallet Address</PanelTitle>
              <PanelValue>{tokenData.walletAddress}</PanelValue>
              <PanelHelp>Save your private key below to import your creator wallet</PanelHelp>
            </Panel>

            <Panel>
              <PanelTitle>Your Private Key (SAVE THIS NOW!)</PanelTitle>
              <PanelValue>{tokenData.privateKey}</PanelValue>
              <CopyKeyButton onClick={handleCopyPrivateKey}>
                {copiedKey ? '✅ Copied!' : '📋 Copy Private Key'}
              </CopyKeyButton>
            </Panel>
          </>
        )}

        <TokenInfo>
          <TokenName>{tokenData.name} (${tokenData.symbol})</TokenName>
          <TokenAddress onClick={handleCopyAddress} style={{ cursor: 'pointer' }}>
            {tokenData.contractAddress}
          </TokenAddress>
        </TokenInfo>

        {/* Four primary actions in a tidy 2x2 grid */}
        <ButtonGrid>
          <CopyButton onClick={handleCopyCardToClipboard}>
            {copiedCard ? 'Copied!' : 'Copy Card to Share on X'}
          </CopyButton>
          <ShareButton onClick={() => handleShare('twitter')}>
            Share on X
          </ShareButton>
          <CopyButton onClick={handleCopyLink}>
            {copiedLink ? 'Copied!' : 'Copy Buy Link'}
          </CopyButton>
          <ShareButton onClick={handleMintCard} disabled={isMinting} style={{ background: isMinting ? '#6b7280' : '#7c3aed' }}>
            {isMinting ? (
              'Minting Card...'
            ) : (
              <>
                Mint Creator Card
                <ButtonSubtitle>(0.0005 ETH)</ButtonSubtitle>
              </>
            )}
          </ShareButton>
        </ButtonGrid>

        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <CloseButton onClick={handleClose}>Close</CloseButton>
        </div>
      </ModalContainer>
    </Overlay>
  );
};

